import { components, paths } from "@api/api";
import useAxios from "@hooks/useFetch";
import { useMemo } from "react";
import { KeywordBase, KeywordTableColumn, useGetKeywordBaseData } from "@api/useGetKeywordBaseData";
import { RespondentType } from "@models/RespondentType";
import { useGetRespondentType } from "@core/CommonDataContext";
import { getFormattedThousand } from "@datassential/platform-ui-lib";
import { Metric } from "@models/Metric";

const REQUEST_PATH: keyof paths = '/api/Search/GetPeople';
export type HomePeopleRawNormRequestPayload = components["schemas"]["FlavorTracker.Entities.DTO.SearchPage.SearchPageMetricRequest"];
type ResponseData = components["schemas"]["FlavorTracker.Entities.DTO.SearchPage.SearchPagePeopleResponse"][];

export type HomePeopleRawNormDataItem = KeywordBase & {
    columns: Map<RespondentType, KeywordTableColumn<RespondentType>>;
}

export function useHomePeopleRawNormData(payload: HomePeopleRawNormRequestPayload) {
    const getKeywordBaseData = useGetKeywordBaseData();
    const getRespondentType = useGetRespondentType();

    const isBaseSizeView = useMemo(
        () => payload.metric === null,
        [payload.metric]
    );

    const isPolarityView = useMemo(
        () => payload.metric === Metric.PolarityScore,
        [payload.metric]
    );

    const response = useAxios<ResponseData, HomePeopleRawNormRequestPayload>({
        url: REQUEST_PATH,
        method: 'post',
        payload,
    });

    const data = useMemo(
        (): HomePeopleRawNormDataItem[] => {
            return (response.data ?? []).map(item => {
                const itemBase = getKeywordBaseData(item.id ?? 0);
                const columns = new Map<RespondentType, KeywordTableColumn<RespondentType>>();

                (item.dataByRespondentType ?? []).forEach(i => {
                    const colData = getRespondentType(i.id ?? RespondentType.NoData);

                    if (isBaseSizeView) {
                        columns.set(i.id ?? RespondentType.NoData, {
                            id: colData?.id ?? RespondentType.NoData,
                            name: colData?.name ?? '',
                            value: i.base ?? null,
                            tableValue: getFormattedThousand(i.base),
                            copyTableValue: `${i.base}`,
                            sortValue: i.base ?? null,
                            norm: null,
                        });
                    } else if (isPolarityView) {
                        const value = i?.norm ?? null;

                        columns.set(i.id ?? RespondentType.NoData, {
                            id: colData?.id ?? RespondentType.NoData,
                            name: colData?.name ?? '',
                            value,
                            tableValue: value !== null ? getFormattedThousand(value) : '-',
                            copyTableValue: value !== null ? `${value}` : '-',
                            sortValue: value,
                            norm: value,
                        });
                    } else {
                        const value = i?.raw ?? null;

                        columns.set(i.id ?? RespondentType.NoData, {
                            id: colData?.id ?? RespondentType.NoData,
                            name: colData?.name ?? '',
                            value,
                            tableValue: value !== null ? `${value}%` : '-',
                            copyTableValue: value !== null ? `${value}%` : '-',
                            sortValue: i?.originalRaw ?? null,
                            norm: i?.norm ?? null,
                        });
                    }
                });

                return {
                    ...itemBase,
                    columns,
                };
            });
        },
        [response.data, getKeywordBaseData, getRespondentType, isBaseSizeView, isPolarityView]
    );

    return {
        ...response,
        data,
    };
}